/**
 * useAdminGymRequests Hook
 *
 * Custom hook for admin dashboard to fetch all gym requests.
 * Supports optional status filtering for managing requests.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  type DocumentData,
  type Unsubscribe,
  type Query,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import type { GymRequest, GymRequestStatus } from '@flash/shared';

/**
 * Convert Firestore document to GymRequest type
 */
const docToGymRequest = (docId: string, docData: DocumentData): GymRequest => {
  return {
    id: docId,
    requesterId: docData['requesterId'] as string,
    status: docData['status'] as GymRequestStatus,
    name: docData['name'] as string,
    description: docData['description'] as string,
    address: docData['address'] as string,
    city: docData['city'] as string,
    state: docData['state'] as string,
    country: docData['country'] as string,
    adminNotes: (docData['adminNotes'] as string | null) ?? null,
    reviewedBy: (docData['reviewedBy'] as string | null) ?? null,
    reviewedAt: docData['reviewedAt'] ?? null,
    gymId: (docData['gymId'] as string | null) ?? null,
    createdAt: docData['createdAt'],
    updatedAt: docData['updatedAt'],
  };
};

/**
 * Options for useAdminGymRequests hook
 */
interface UseAdminGymRequestsOptions {
  /** Filter by status, or 'all' to get all requests */
  status?: GymRequestStatus | 'all';
  /** Whether to use real-time listener (default: true) */
  realtime?: boolean;
}

/**
 * State interface for admin gym requests query
 */
interface UseAdminGymRequestsState {
  /** List of gym requests */
  requests: GymRequest[];
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useAdminGymRequests hook
 */
interface UseAdminGymRequestsResult extends UseAdminGymRequestsState {
  /** Refetch requests from Firestore */
  refetch: () => Promise<void>;
  /** Count of requests by status */
  statusCounts: Record<GymRequestStatus | 'total', number>;
}

/**
 * Hook to fetch all gym requests for admin dashboard
 *
 * @param options - Optional configuration for filtering
 * @returns Object containing requests, loading state, error, and counts
 *
 * @example
 * ```tsx
 * // Fetch all pending requests
 * const { requests, loading, statusCounts } = useAdminGymRequests({ status: 'pending' });
 *
 * // Fetch all requests
 * const { requests } = useAdminGymRequests({ status: 'all' });
 *
 * if (loading) return <LoadingSpinner />;
 *
 * return (
 *   <div>
 *     <p>{statusCounts.pending} pending requests</p>
 *     <RequestList requests={requests} />
 *   </div>
 * );
 * ```
 */
export const useAdminGymRequests = (
  options: UseAdminGymRequestsOptions = {}
): UseAdminGymRequestsResult => {
  const { status = 'all', realtime = true } = options;
  const { user } = useAuth();

  const [state, setState] = useState<UseAdminGymRequestsState>({
    requests: [],
    loading: true,
    error: null,
  });

  /**
   * Build the Firestore query based on options
   */
  const buildQuery = useCallback((): Query<DocumentData> | null => {
    if (!user) return null;

    const db = getFirebaseDb();
    const requestsRef = collection(db, 'gymRequests');

    if (status === 'all') {
      return query(requestsRef, orderBy('createdAt', 'desc'));
    }

    return query(requestsRef, where('status', '==', status), orderBy('createdAt', 'desc'));
  }, [user, status]);

  /**
   * Fetch requests from Firestore (one-time)
   */
  const fetchRequests = useCallback(async (): Promise<void> => {
    const requestsQuery = buildQuery();

    if (!requestsQuery) {
      setState({ requests: [], loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const snapshot = await getDocs(requestsQuery);

      const requests: GymRequest[] = snapshot.docs.map((docSnapshot) =>
        docToGymRequest(docSnapshot.id, docSnapshot.data())
      );

      setState({ requests, loading: false, error: null });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch gym requests';
      console.error('Error fetching admin gym requests:', err);
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, [buildQuery]);

  // Real-time subscription effect
  useEffect(() => {
    const requestsQuery = buildQuery();

    if (!requestsQuery) {
      setState({ requests: [], loading: false, error: null });
      return;
    }

    // For non-realtime mode, just fetch once
    if (!realtime) {
      fetchRequests();
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    // Set up real-time listener
    const unsubscribe: Unsubscribe = onSnapshot(
      requestsQuery,
      (snapshot) => {
        const requests: GymRequest[] = snapshot.docs.map((docSnapshot) =>
          docToGymRequest(docSnapshot.id, docSnapshot.data())
        );

        setState({ requests, loading: false, error: null });
      },
      (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch gym requests';
        console.error('Error in admin gym requests subscription:', err);
        setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [buildQuery, realtime, fetchRequests]);

  /**
   * Calculate counts by status
   */
  const statusCounts: Record<GymRequestStatus | 'total', number> = {
    pending: state.requests.filter((r) => r.status === 'pending').length,
    approved: state.requests.filter((r) => r.status === 'approved').length,
    rejected: state.requests.filter((r) => r.status === 'rejected').length,
    changes_requested: state.requests.filter((r) => r.status === 'changes_requested').length,
    total: state.requests.length,
  };

  return {
    ...state,
    refetch: fetchRequests,
    statusCounts,
  };
};
